import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components/macro';
import Layout from 'components/common/Layout';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { history } from 'store/helpers';
import {
  ticketActions,
  unlockActions,
  alertsActions,
  curBookingsActions,
  bookingInfoActions,
  workdeskFilterActions,
  assignUnassignActions,
} from 'store/actions';
import loadingImg from 'assets/images/spinner.gif';
import { createSelector } from '@reduxjs/toolkit';
import { mainPageFormDef } from 'form-defs/mainpage.form-def';
import MainTextInput from 'components/main/MainTextInput';
import MainDropdown from 'components/main/MainDropdown';
import {
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  DialogTitle,
  Tooltip,
  Popover,
} from '@material-ui/core';
import {
  Ticket,
  TicketDataFromMainPage,
  ToAutosaveApiI,
} from 'store/models/Ticket';
import { userDataSelector } from 'store/selectors';
import {
  curBookingsBookingsSelector,
  isMultiBookingSelector,
  mainBookingMSelector,
} from 'store/selectors/cur-bookings.selector';
import { commonPagesSelector } from 'store/selectors/cur-bookings-attachments.selector';
import {
  originalFormTicketSelector,
  isLoadingSubmitBookingSelector,
  ticketIsLoadingSelector,
  canEditTicketSelector,
  ticketFormTicketSelector,
  ticketSelector,
  autoMarkIsSplitErrorSelector,
  checkingBookingInfoSelector,
  ticketErrorSelector,
  ticketOnLoadFormToAutosaveApiDataSelector,
} from 'store/selectors/ticket.selector';
import MainPageAlert from 'store/models/MainPageAlert';
import SearchParamsHelper from 'store/helpers/SearchParamsHelper';
import { Statuses } from 'store/models/Statuses';
import { SaveRounded, PublishRounded } from '@material-ui/icons';
import { timeFormatters } from 'utils/time-formatters';
import { MatUiCheckbox } from 'components/main';
import { SectionI, FieldI } from 'form-defs/form-def.interface';
import { ticketAttachmentsRecycledAttachmentPartIdsSelector } from 'store/selectors/ticket-attachment.selector';

interface MainPageProps {}
export const AUTOSAVE_INTEVAL = 60000;

// NOTE: not sure if ticketDataSelector and originalFormTicketSelector should be in the MainPage class or not

const autosaveInfoSelector = createSelector(
  ticketSelector,
  (ticket) => {
    if (!ticket) return {};
    return {
      isAutosaving: ticket.isAutosaving,
      isAutosaveSuccess:
        ticket?.data?.autoSave?.updateDate !== undefined,
      isAutosaveFailed: ticket.autoSaveStatus === 'FAILED',
      lastAutosaveAtText:
        ticket?.data?.autoSave?.updateDate &&
        timeFormatters.getAutoSavedTimeText(
          ticket?.data?.autoSave?.updateDate,
        ),
    };
  },
);

export const SectionTitle: React.FC<{
  title: string | undefined;
  withoutMarginBottom?: boolean;
}> = ({ title, withoutMarginBottom = false }) => {
  return (
    <SSectionTitle withoutMarginBottom={withoutMarginBottom}>
      {title}
    </SSectionTitle>
  );
};

/**
 * if booking no or subject change, send changeFlag
 * @param props
 */

/**
 * TIPS: if you have es7 snippets install, you can type rface to get create component template
 */
const MainPage: React.FC<MainPageProps> = () => {
  const [userIsActive, setUserIsActive] = useState(true);
  const dispatch = useDispatch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableDispatch = useCallback(dispatch, []);
  const justSavedWithSubmit = useRef(false);
  const [buttonClick, setButtonClick] = useState<string | undefined>(
    undefined,
  );
  const hasChangeOrSaveClickedRef = useRef(false);
  const location = useLocation();
  const { fromCE, inbox } = useMemo(
    () => SearchParamsHelper.getCEParams(location),
    [location],
  );
  const { ticketId } = useParams<{ ticketId: string }>();

  // passed through apiToForm already
  const formattedTicket: TicketDataFromMainPage | null = useSelector(
    originalFormTicketSelector,
  ); // from the api

  const formTicket = useSelector(ticketFormTicketSelector); // current form ticket
  const mainBookingM = useSelector(mainBookingMSelector);
  const isLoading = useSelector(ticketIsLoadingSelector);
  const autosaveInfo = useSelector(autosaveInfoSelector);

  const ticketOnLoadFormToAutosaveApiData = useSelector(
    ticketOnLoadFormToAutosaveApiDataSelector,
  );
  const isSaving = useMemo(
    () => autosaveInfo?.isAutosaving ?? false,
    [autosaveInfo],
  );
  const curBookings = useSelector(curBookingsBookingsSelector);

  const curBookingsNos = useMemo(
    () => curBookings && curBookings.map((b) => b.bookingNo),
    [curBookings],
  );
  const curBookingsNosStr: string | null = useMemo(
    () =>
      curBookingsNos && curBookingsNos.length > 0
        ? JSON.stringify(curBookingsNos)
        : null,
    [curBookingsNos],
  );
  const bookingsCount = useMemo(() => curBookings?.length || 0, [
    curBookings,
  ]);
  const authUser = useSelector(userDataSelector);
  const isLoadingSubmitBooking = useSelector(
    isLoadingSubmitBookingSelector,
  );
  const autoMarkIsSplitError = useSelector(
    autoMarkIsSplitErrorSelector,
  );
  const isMultiBooking = useSelector(isMultiBookingSelector);
  const canEdit = useSelector(canEditTicketSelector);
  const canEditRef = useRef(false);
  const toUpdateData = useRef<any>(null);
  const commonPages = useSelector(commonPagesSelector);

  // to watch checkingBookingInfo event
  const checkingBookingInfo = useSelector(
    checkingBookingInfoSelector,
  );

  const ticketError = useSelector(ticketErrorSelector);

  const recycledAttachmentPartIds = useSelector(
    ticketAttachmentsRecycledAttachmentPartIdsSelector,
  );

  const formToAutosaveApiRef = useRef<any>(null);
  const formattedTicketRef = useRef(formattedTicket);
  const recycledAttachmentPartIdsRef = useRef(
    recycledAttachmentPartIds,
  );
  const formTicketAssignee = formTicket?.assignee;
  const formTicketRef = useRef<any>(formTicket);
  const formTicketPrevBookingNoRef = useRef<undefined | string>(
    undefined,
  );

  const authUserRef = useRef(authUser);
  const curBookingsRef = useRef(curBookings);

  const formTicketBookingNo = formTicket?.bookingNo;

  useEffect(() => {
    formTicketRef.current = formTicket;
  }, [formTicket]);
  useEffect(() => {
    authUserRef.current = authUser;
  }, [authUser]);
  useEffect(() => {
    formattedTicketRef.current = formattedTicket;
  }, [formattedTicket]);
  useEffect(() => {
    curBookingsRef.current = curBookings;
  }, [curBookings]);
  useEffect(() => {
    recycledAttachmentPartIdsRef.current = recycledAttachmentPartIds;
  }, [recycledAttachmentPartIds]);

  const prevcheckingBookingInfo = useRef(checkingBookingInfo);

  useEffect(() => {
    if (
      prevcheckingBookingInfo.current === false &&
      checkingBookingInfo === true
    ) {
      // just finished firing the event useAllBookingsFromEmail
      checkingRef.current = true;
    } else if (
      prevcheckingBookingInfo.current === true &&
      checkingBookingInfo === false
    ) {
      checkingRef.current = false;
    }
    prevcheckingBookingInfo.current = checkingBookingInfo;
  }, [checkingBookingInfo]);

  useEffect(() => {
    console.info(`*** autosave interval (ms): `, AUTOSAVE_INTEVAL);
  }, []);

  useEffect(() => {
    canEditRef.current = canEdit;
  }, [canEdit]);

  /**
   * clear top left booking no. blank top left.
   */
  // useEffect(() => {
  //   console.debug(`if multi, clear top left booking no`, {
  //     isMultiBooking,
  //   });
  //   if (isMultiBooking) {
  //     stableDispatch(ticketActions.clearTopLeftBookingNo());
  //   } else {
  //     stableDispatch(
  //       ticketActions.updateBottomBookingNoWithTopLeft(),
  //     );
  //   }
  // }, [isMultiBooking, stableDispatch]);
  const onloadFormToAutosaveApiDataRef = useRef<
    undefined | ToAutosaveApiI
  >(undefined);
  useEffect(() => {
    // TODO: fix because bookings is not in there yet
    if (ticketOnLoadFormToAutosaveApiData) {
      onloadFormToAutosaveApiDataRef.current = ticketOnLoadFormToAutosaveApiData;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketOnLoadFormToAutosaveApiData]);

  const handleSaveTicket = useCallback(
    async (validate = false) => {
      console.debug(`handleSaveTicket callback`);
      // has to use the data in the ref because have to call save when unmoun the page too
      if (!formToAutosaveApiRef.current) return;
      const { data, changeFlag } = formToAutosaveApiRef.current;
      const oldStr =
        onloadFormToAutosaveApiDataRef.current &&
        JSON.stringify(onloadFormToAutosaveApiDataRef.current);
      const newStr = data && JSON.stringify(data);
      if (oldStr !== newStr) {
        hasChangeOrSaveClickedRef.current = true;
      }
      console.log(
        'hasChangeOrSaveClickedRef:',
        hasChangeOrSaveClickedRef.current,
        'ticket data to save',
        {
          old: onloadFormToAutosaveApiDataRef.current,
          new: data,
          oldStr,
          newStr,
        },
      );
      await stableDispatch(
        ticketActions.saveTicket(
          ticketId,
          data,
          changeFlag,
          validate,
        ),
      );
    },
    [ticketId, stableDispatch],
  );

  /**
   * on unmount
   */
  const handleOnUnmount = useCallback(async () => {
    const canNonAdminEdit = Ticket.canNonAdminEditTicket(
      formTicketRef.current?.ticket,
      authUserRef.current,
    );
    console.debug(
      `callasync IfCanEdit (currently save if canNonAdminEdit instead of canEditRef)`,
      {
        canEditRef: canEditRef.current,
        canNonAdminEdit,
        formToAutosaveApiRef: formToAutosaveApiRef.current,
        formTicketRef: formTicketRef.current?.ticket,
        authUserRef: authUserRef.current,
      },
    );
    stableDispatch(
      workdeskFilterActions.restoreSelectedInboxesFromBeforeLoad(),
    );
    if (!justSavedWithSubmit.current && canNonAdminEdit) {
      await handleSaveTicket(); // save before reset
    }

    stableDispatch(ticketActions.resetTicket());
    stableDispatch(bookingInfoActions.reset());
    stableDispatch(curBookingsActions.reset());
  }, [stableDispatch, handleSaveTicket]);

  /**
   * on init load ticket
   * reset ticket when unmount
   */
  useEffect(() => {
    stableDispatch(
      ticketActions.loadTicket(ticketId, 'INIT', fromCE, inbox),
    );
    // clear unlock file map when open new ticket (because partId might be duplicated across the tickets)
    stableDispatch(unlockActions.clearUnlockFileStatusMap());

    return () => {
      console.debug(`MainPage: unmount`);
      handleOnUnmount();
    };
  }, [stableDispatch, handleOnUnmount, ticketId, fromCE, inbox]);

  const [showMainPageAlert, setShowMainPageAlert] = useState(false);

  const didFirstLoadCurBookings = useRef<boolean>(false); // for tracking validating booking no / fetch info

  const checkingRef = useRef(false); // for tracking validating booking no / fetch info
  const [didApiChangeMarkSplit, setDidApiChangeMarkSplit] = useState(
    false,
  ); // for tracking validating booking no / fetch info

  /**
   * note this is triggered after save as well
   */
  const handleApiTicketChange = useCallback(async () => {
    const formTicket = formTicketRef.current;

    if (formattedTicket && Object.keys(formattedTicket).length > 0) {
      const merged = Ticket.mergeFormData(
        formTicket,
        formattedTicket,
      );
      await stableDispatch(ticketActions.setFormTicket(merged));

      const bookingNos = merged.ticket.bookingNos || [];

      const isMulti = bookingNos.length > 0;
      let bookingNoToCheckSplit;
      if (isMulti) {
        // if multi takes the first from bookingNos
        bookingNoToCheckSplit = bookingNos[0];
      } else {
        // if single, takes main bookingNo
        bookingNoToCheckSplit = merged.bookingNo; // or merged.ticket.bookingNo
      }

      // not sure if should move this to the action
      if (!formattedTicket.savedAlready && bookingNoToCheckSplit) {
        // still automark even if user cannot edit ticket
        // // ********** related to booking/info **********
        console.group(
          'handleProcessBookingInfo: handleApiTicketChange',
        );
        console.debug(`checkingRef: ${checkingRef.current}`);
        if (!checkingRef.current) {
          checkingRef.current = true;
          await stableDispatch(
            bookingInfoActions.autoMarkIsSplit(bookingNoToCheckSplit),
          );
          // for tracking validating booking no / fetch info
          setDidApiChangeMarkSplit(true);
          checkingRef.current = false;
        }
        console.groupEnd();
      }
    }
  }, [stableDispatch, formattedTicket]);

  /**
   * validate main booking and automark is split when formatted ticket changes
   */
  const handleProcessBookingInfo = useCallback(async () => {
    // // ********** related to booking/info **********
    console.debug(
      `check for handleAutomarkIsSplit: `,
      'formTicketBookingNo: ',
      formTicketBookingNo,
      'curBookingsNosStr',
      curBookingsNosStr,
      'formTicketPrevBookingNoRef.current',
      formTicketPrevBookingNoRef.current,
      'checkingRef.current',
      checkingRef.current,
      'didApiChangeMarkSplit',
      didApiChangeMarkSplit,
      'didFirstLoadCurBookings',
      didFirstLoadCurBookings,
    );
    if (!checkingRef.current) {
      // validate main booking no (top left)
      if (
        formTicketBookingNo &&
        formTicketPrevBookingNoRef.current !== formTicketBookingNo
      ) {
        console.group(
          'handleProcessBookingInfo: validate main and auto mark split when first booking no changes',
        );
        console.debug(
          `formTicketPrevBookingNoRef.current: ${formTicketPrevBookingNoRef.current} formTicketBookingNo:${formTicketBookingNo}`,
        );
        checkingRef.current = true;
        formTicketPrevBookingNoRef.current = formTicketBookingNo;
        checkingRef.current = false;
        console.groupEnd();
      }
      // validate all current bookings on the bottom right
      if (curBookingsNosStr && !didFirstLoadCurBookings.current) {
        console.group(
          'handleProcessBookingInfo: validate all cur bookings on load',
        );
        checkingRef.current = true;
        await stableDispatch(
          curBookingsActions.validateAllCurBookings(),
        );
        checkingRef.current = false;
        // important: do this after
        didFirstLoadCurBookings.current = true;
        console.groupEnd();
      }
    }
  }, [
    stableDispatch,
    formTicketBookingNo,
    curBookingsNosStr,
    didApiChangeMarkSplit,
  ]);

  useEffect(() => {
    handleProcessBookingInfo();
  }, [handleProcessBookingInfo]);

  const checkIfIsAdminAccess = useCallback(() => {
    const formTicket = formTicketRef.current;
    const authUser = authUserRef.current;
    const canEdit = Ticket.canEditTicket(
      formTicket?.ticket,
      authUser,
    );
    console.debug(`checkIfIsAdminAccess`);
    if (canEdit) {
      const canNonAdminEdit = Ticket.canNonAdminEditTicket(
        formTicket?.ticket,
        authUser,
      );
      console.debug(`canNonAdminEdit: ${canNonAdminEdit}`);
      const alertMessage = `You're editting using admin access`;
      if (!canNonAdminEdit) {
        stableDispatch(alertsActions.error(alertMessage, true));
      } else {
        stableDispatch(
          alertsActions.clear({
            message: alertMessage,
          }),
        );
      }
    }
  }, [stableDispatch]);

  useEffect(() => {
    checkIfIsAdminAccess();
  }, [formTicketAssignee, checkIfIsAdminAccess]);

  useEffect(() => {
    handleApiTicketChange();
  }, [formattedTicket, handleApiTicketChange]);

  const canMarkSi = useMemo(
    () =>
      formattedTicket &&
      Statuses.canMarkSiNonSi(formattedTicket.ticket.status),
    [formattedTicket],
  );

  const canAssign = useMemo(
    () =>
      formattedTicket &&
      formattedTicket.ticket &&
      Ticket.canAssignTicket(formattedTicket.ticket),
    [formattedTicket],
  );

  const mainPageAlertDetail = useMemo(() => {
    const detail = new MainPageAlert(
      formattedTicket,
      authUserRef.current,
      stableDispatch,
      fromCE,
      inbox,
      ticketError,
    ).getAlertDetail();
    console.debug(`mainPageAlertDetail: `, detail);
    return detail;
  }, [formattedTicket, stableDispatch, fromCE, inbox, ticketError]);

  useEffect(() => {
    setShowMainPageAlert(!!mainPageAlertDetail);
  }, [mainPageAlertDetail]);

  useEffect(() => {
    console.debug(`mainPageAlertDetail: `, mainPageAlertDetail);
  }, [mainPageAlertDetail]);

  useEffect(() => {
    formToAutosaveApiRef.current = Ticket.formToAutosaveApi(
      {
        ...formTicket,
        bookings: curBookings,
        commonPages,
      },
      formattedTicketRef.current,
      recycledAttachmentPartIdsRef.current,
    );
  }, [curBookings, formTicket, commonPages]);

  /**
   * autosave
   */
  // // ********** related to booking/info **********
  useEffect(() => {
    if (canEdit) {
      // only autosave if canEdit

      toUpdateData.current = setInterval(() => {
        handleSaveTicket();
      }, AUTOSAVE_INTEVAL);
    } else if (toUpdateData.current) {
      clearInterval(toUpdateData.current);
    }

    return () => {
      if (toUpdateData.current) clearInterval(toUpdateData.current);
    };
    // notmally would do only depends on [] because only at init, but setInterval has to be reset every time the ticket changes, otherwise the data won't be updated
  }, [formTicket, curBookings, canEdit, handleSaveTicket]);

  /**
   * update formTicket
   * @param value
   * @param key
   */
  const handleValueChanged = async (value: any, key?: string) => {
    if (key !== undefined) {
      await dispatch(ticketActions.updateFormTicketAtKey(key, value));
      if (key === 'urgency' || key === 'siKind') {
        handleSaveTicket();
      }
    } else {
      console.warn('handleValueChanged key is undefined');
    }
  };

  /**
   * validate booking no (if key is bookingNo)
   * @param value
   * @param key
   */

  const handleOnBlur = (value: any, key?: string) => {
    if (key !== undefined) {
      dispatch(ticketActions.updateFormTicketAtKey(key, value));
    } else {
      console.warn('handleOnBlur key is undefined');
    }
  };

  const handleSubmit = async (submitAnyway = false) => {
    // now we have setup saving on unmount so not calling here
    handleSaveTicket();

    // dispatch start requesting preview with the data
    const success = await dispatch(
      ticketActions.submitBooking(formTicket, submitAnyway),
    );
    if (success) {
      justSavedWithSubmit.current = true;
      history.push('/workdesk');
    } else {
      justSavedWithSubmit.current = false;
    }
  };
  const handleAssignToMe = async () => {
    await dispatch(
      assignUnassignActions.postUnassignTicket(formTicket?.id),
    );
    dispatch(assignUnassignActions.postAssignTicket(formTicket?.id));
  };

  /**
   * loading screen.
   * TODO: consider changing to other style of loading
   */

  const renderLoading = () => {
    return (
      <SLoadingWrapper>
        <SText>
          <SMainText>OCPS</SMainText>
          <SSubText>Web Agent II</SSubText>
        </SText>
        <SSpinnerImg src={loadingImg} />
      </SLoadingWrapper>
    );
  };
  const renderEachField = (field: FieldI) => {
    switch (field.type) {
      case 'TEXT':
        // note: haven't impliment using handleOnFocus here
        return (
          <MainTextInput
            name={field.name}
            label={field.label}
            disabled={!canEdit || field.disabled}
            isMultiRow={field.isMultiRow}
            value={
              formTicket && field.name ? formTicket[field.name] : ''
            }
            labelOnLeft={field.labelOnLeft}
            rows={field.rows}
            handleAfterChange={(value) =>
              handleValueChanged(value, field.name)
            }
            placeholder={field.placeholder}
          />
        );
      case 'SUB_FIELDS':
        // note: haven't impliment using handleOnFocus here
        return field && field.fields
          ? renderSectionFields(field.fields, true)
          : null;
      case 'DROPDOWN':
        // note: haven't impliment using handleOnFocus here
        return (
          <MainDropdown
            label={field.label}
            name={field.name}
            recommendedValue={
              formTicket &&
              field?.recommendedValueField &&
              formTicket[field?.recommendedValueField]
            }
            options={field.options}
            defaultValue={
              (formTicket &&
                field?.defaultValueField &&
                formTicket[field?.defaultValueField]) ??
              ''
            }
            handleChange={(value) =>
              handleValueChanged(value, field.name)
            }
            disabled={!canEdit}
          />
        );
      case 'CHECKBOX':
        // note: haven't impliment using handleOnFocus here
        return (
          <MatUiCheckbox
            label={field.label}
            name={field.name}
            value={formTicket && formTicket[field.name]}
            handleChange={(checked) =>
              handleValueChanged(checked, field.name)
            }
            disabled={!canEdit || field.disabled}
          />
        );
      case 'CUSTOM':
        if (!field) return null;
        if (field.renderWithArgs) {
          // note: can pass handleFocus in but not needed yet
          return field.renderWithArgs({
            value: formTicket && formTicket[field.name],
            dispatch,
            isLoading: formTicket
              ? field.loadingName
                ? formTicket[field.loadingName] // TODO: check if should be formattedTicket or formTicket
                : !formTicket[field.name]
              : true,
            field,
            handleValueChanged,
            hasChangeOrSaveClickedRef,
            handleOnBlur,
            formTicket,
            canEdit,
            canMarkSi,
            canAssign,
            isMultiBooking,
            bookingsCount,
            autoMarkIsSplitError,
            mainBookingM,
            userIsActive,
            setUserIsActive,
            buttonClick,
            setButtonClick,
            handleAssignToMe,
          });
        } else if (field.renderWithoutArgs) {
          return field.renderWithoutArgs();
        } else return null;
      default:
        return null;
    }
  };
  const renderSectionFields = (
    sectionFields?: FieldI[],
    oneRow = false,
  ) => {
    if (!sectionFields) return null;
    return (
      <SSectionFields className={oneRow ? 'one-row' : ''}>
        {sectionFields.map((field) => (
          <SSectionField key={field.name}>
            {renderEachField(field)}
          </SSectionField>
        ))}
      </SSectionFields>
    );
  };

  const renderAutosaveInfo = () => {
    let text = '';
    if (autosaveInfo.isAutosaving) {
      text = 'Saving...';
    } else if (autosaveInfo.isAutosaveSuccess) {
      text = `Last saved ${autosaveInfo.lastAutosaveAtText}`;
    } else if (autosaveInfo.isAutosaveFailed) {
      text = 'Failed to save';
    }
    return (
      <Tooltip
        title={`Autosave every ${AUTOSAVE_INTEVAL / 1000} seconds`}
      >
        <SIsAutosaving>{text}</SIsAutosaving>
      </Tooltip>
    );
  };

  const handleCloseMainPageAlert = () => {
    setShowMainPageAlert(false);
    if (mainPageAlertDetail && mainPageAlertDetail.onClose) {
      mainPageAlertDetail.onClose();
    }
  };

  const [
    submitAnywayAnchorEl,
    setSubmitAnywayAnchorEl,
  ] = useState<HTMLButtonElement | null>(null);

  const handleToggleSecretSubmitAnyway = (e) => {
    e.preventDefault(); // prevent showing the normal right click menu (ex: inspect)
    setSubmitAnywayAnchorEl(e.currentTarget);
  };
  const handleCloseSubmitAnywayAnchorEl = () => {
    setSubmitAnywayAnchorEl(null);
  };
  const handleClickSubmitAnyway = () => {
    handleCloseSubmitAnywayAnchorEl();
    handleSubmit(true);
  };

  const renderMain = () => {
    return (
      <SMainRoot>
        {renderAutosaveInfo()}
        <SDialog
          fullWidth
          maxWidth="xs"
          open={showMainPageAlert}
          onClose={handleCloseMainPageAlert}
        >
          <DialogTitle>Ticket Information</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {mainPageAlertDetail?.message}
            </DialogContentText>
          </DialogContent>

          {mainPageAlertDetail?.buttons && (
            <DialogActions>
              {mainPageAlertDetail?.buttons.map((button) => (
                <Button
                  key={button.label}
                  onClick={() => {
                    if (button.onClick) {
                      button.onClick();
                    }
                    handleCloseMainPageAlert();
                  }}
                  autoFocus={button.autoFocus}
                >
                  {button.label}
                </Button>
              ))}
            </DialogActions>
          )}
        </SDialog>
        <SMainPanelWrapper>
          {[
            {
              section: mainPageFormDef.leftSection,
              label: 'left',
            },
            { section: mainPageFormDef.rightSection, label: 'right' },
          ].map((eachSection) => {
            return (
              <SMainPanel key={eachSection.label}>
                {eachSection.section.map(
                  (section: SectionI, index: number) => {
                    return (
                      <SSectionWrapper
                        key={
                          section.title ||
                          `${eachSection.label}-section${index}`
                        }
                      >
                        {!section.hideTitle && (
                          <SectionTitle title={section.title} />
                        )}
                        {renderSectionFields(section.fields)}
                      </SSectionWrapper>
                    );
                  },
                )}
              </SMainPanel>
            );
          })}
        </SMainPanelWrapper>
        <SConfirmFooter>
          <FooterButtons>
            {[
              {
                variant: 'outlined',
                label: 'Save',
                onClick: () => {
                  hasChangeOrSaveClickedRef.current = true;
                  setButtonClick('SAVE');
                  handleSaveTicket(true);
                },
                disabled: !canEdit || isSaving,
                startIcon: <SaveRounded />,
              },
              {
                ariaDescribedby: 'submit-anyway',
                variant: 'contained',
                label: 'Send Now',
                onClick: () => {
                  setButtonClick('SEND_NOW');
                  handleSubmit();
                },
                onContextMenu: handleToggleSecretSubmitAnyway,
                disabled: !canEdit || isLoadingSubmitBooking,
                startIcon: isLoadingSubmitBooking ? (
                  <SLoadingImage src={loadingImg} width="25px" />
                ) : (
                  <PublishRounded />
                ),
              },
            ].map((item: any) => (
              <Button
                aria-describedby={item.ariaDescribedby}
                key={item.label}
                variant={item.variant}
                color="primary"
                disableElevation
                onClick={item.onClick}
                disabled={item.disabled}
                startIcon={item.startIcon}
                onContextMenu={item.onContextMenu || null} // right click
              >
                {item.label}
              </Button>
            ))}
            <Popover
              id={'submit-anyway'}
              open={Boolean(submitAnywayAnchorEl)}
              anchorEl={submitAnywayAnchorEl}
              onClose={handleCloseSubmitAnywayAnchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              {/* only works if not empty and no duplicate */}
              <Tooltip title="Only works when no empty and no duplicate">
                <Button
                  startIcon={<PublishRounded />}
                  onClick={handleClickSubmitAnyway}
                  disabled={!canEdit || isLoadingSubmitBooking}
                >
                  Submit Anyway
                </Button>
              </Tooltip>
            </Popover>
          </FooterButtons>
        </SConfirmFooter>
      </SMainRoot>
    );
  };
  return (
    <Layout pageName="Main">
      {isLoading ? renderLoading() : renderMain()}
    </Layout>
  );
};

export default MainPage;

const SLoadingWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;
const SMainText = styled.div`
  font-weight: bold;
  font-size: 60px;
`;
const SSubText = styled.div`
  font-size: 14px;
  text-align: center;
`;
const SSpinnerImg = styled.img`
  width: 3rem;
  margin-top: 1rem;
`;
const SText = styled.div`
  color: ${(props) => props.theme.palette.primary.main};
`;
const SMainRoot = styled.div`
  position: relative;
  display: grid;
  height: 100%;
`;
const SMainPanel = styled.div`
  padding: 1.25rem;
`;

const SSectionWrapper = styled.div`
  margin-bottom: 1.8rem;
`;
const SSectionTitle = styled.div`
  border-left: 6px solid
    ${(props) => props.theme.palette.primary.main};
  padding: 0 0.5rem;
  font-size: ${(props) => props.theme.typography.fontSize.title};
  font-weight: ${(props) => props.theme.typography.fontWeight.title};
  margin-bottom: ${(props: { withoutMarginBottom: boolean }) =>
    props.withoutMarginBottom ? '0' : '0.75rem'};
`;
const SSectionField = styled.div``;
const SSectionFields = styled.div.attrs((props) => ({
  className: props.className,
}))`
  .one-row {
    display: flex;
    margin-bottom: 1rem;
  }
  &:not(.one-row) > ${SSectionField} + ${SSectionField} {
    margin-top: 0.5rem;
  }
  &.one-row > ${SSectionField} + ${SSectionField} {
    margin-left: 0.8rem;
  }
`;
const SConfirmFooter = styled.div`
  // position: absolute;
  // bottom: 0;
  // left: 0;
  // right: 0;
  height: 4.5rem;
  background-color: #fff;
  box-shadow: 0 0 5px 0 #d3d3d3;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const FooterButtons = styled.div`
  margin-right: 2rem;
  & > .MuiButton-root + .MuiButton-root {
    margin-left: 1rem;
  }
`;
const SMainPanelWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(250px, 30%) 1fr;
  grid-gap: 1rem;
  // don't do height 100%
  & > * {
    background: ${(props) => props.theme.palette.background.paper};
    border-radius: 0.25rem;
  }
  overflow: auto;
  margin: 1rem;
`;

const SIsAutosaving = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  text-align: right;
  margin-top: -1rem;
  height: 0.8rem;
  color: grey; // TODO: might change shade of grey (use the color in the theme)
`;
const SDialog = styled(Dialog)`
  & .MuiDialog-paper {
    /* position: absolute;
    top: 0;
    right: 0; */
  }
`;
const SLoadingImage = styled.img``;
